import { usePlayerStore } from '../stores/playerStore';
import { notificationService } from './notificationService';
import { audioEngine } from './audioEngine';
import { logger } from '../utils/logger';

interface TimerState {
  startTime: number;
  duration: number;
}

class SleepTimerService {
  private timerState: TimerState | null = null;
  private checkInterval: number | null = null;
  private readonly STORAGE_KEY = 'sleepTimerState';
  private readonly CHECK_INTERVAL = 1000; // Check every second
  private readonly NOTIFICATION_THRESHOLDS = [300, 60]; // 5 minutes and 1 minute
  private lastCheckTime: number = Date.now();
  /** Guards against the interval, visibilitychange and idle-callback all firing. */
  private completed = false;

  constructor() {
    this.loadState();
    this.startBackgroundCheck();

    // Primary expiry signal. setInterval is throttled to ~1/min in background
    // tabs and suspended entirely on mobile with the screen off — which is
    // exactly when a sleep timer is supposed to fire. The audio pipeline keeps
    // emitting timeupdate throughout, so we check the deadline there too.
    audioEngine.onTick(() => {
      if (this.timerState && this.hasExpired()) {
        logger.info('Sleep timer completed (audio tick)');
        this.handleTimerComplete();
      }
    });

    // Handle page visibility changes
    if (typeof document !== 'undefined' && document) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          // When page becomes visible, check if we missed any timer completions
          const now = Date.now();
          const timeSinceLastCheck = now - this.lastCheckTime;
          this.lastCheckTime = now;

          if (this.timerState) {
            const remainingTime = this.getRemainingTime();
            logger.info(`Sleep timer check - Time since last check: ${timeSinceLastCheck}ms, Remaining time: ${remainingTime}s`);

            // If timer should have completed while in background
            if (this.hasExpired()) {
              logger.info('Sleep timer completed while in background');
              this.handleTimerComplete();
              return;
            }
          }

          this.loadState();
          this.startBackgroundCheck();
        } else {
          // Store the last check time when page becomes hidden
          this.lastCheckTime = Date.now();
          this.saveState();
        }
      });
    }

    // Handle before unload to save state
    if (typeof window !== 'undefined' && window) {
      window.addEventListener('beforeunload', () => {
        this.saveState();
      });
    }

    // Set up periodic state check even in background
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const checkStateInBackground = () => {
        if (document.visibilityState === 'hidden' && this.timerState && this.hasExpired()) {
          this.handleTimerComplete();
        }
        window.requestIdleCallback(checkStateInBackground);
      };
      window.requestIdleCallback(checkStateInBackground);
    }
  }

  private loadState() {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (savedState) {
        this.timerState = JSON.parse(savedState);
        logger.info('Loaded sleep timer state');

        // Validate loaded state
        if (this.hasExpired()) {
          logger.info('Sleep timer expired during load');
          this.handleTimerComplete();
        } else {
          // Still running: the store no longer persists sleepTimer, so publish the
          // remaining time or the countdown would vanish after a reload.
          const remaining = this.getRemainingTime();
          if (remaining !== null) {
            usePlayerStore.getState().setSleepTimer(Math.max(1, Math.ceil(remaining / 60)));
          }
        }
      }
    } catch (error) {
      logger.error('Error loading sleep timer state');
      this.stopTimer();
    }
  }

  private saveState() {
    if (this.timerState) {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.timerState));
        logger.info('Saved sleep timer state');
      } catch (error) {
        logger.error('Error saving sleep timer state');
      }
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private startBackgroundCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = window.setInterval(() => {
      if (!this.timerState) return;

      const remaining = this.getRemainingTime();
      if (remaining === null) {
        this.stopTimer();
        return;
      }

      // Check if timer is complete. This branch was previously unreachable:
      // getRemainingTime() collapsed every expired value to null, so the check
      // above swallowed the expiry and just cancelled the timer instead.
      if (remaining <= 0) {
        logger.info('Sleep timer completed during interval check');
        this.handleTimerComplete();
        return;
      }

      // Show notifications at thresholds
      this.NOTIFICATION_THRESHOLDS.forEach(threshold => {
        if (remaining <= threshold && remaining > threshold - this.CHECK_INTERVAL / 1000) {
          this.showNotification(threshold);
        }
      });
    }, this.CHECK_INTERVAL);
  }

  private handleTimerComplete() {
    if (this.completed) return; // visibilitychange + interval can both fire
    this.completed = true;

    logger.info('Handling sleep timer completion');

    // Stop the audio directly. The old code paused `store.audioRef`, which was
    // always null because nothing ever populated it — so the timer expired
    // silently and the stream kept playing.
    audioEngine.stop();

    // setSleepTimer(0) marks the timer as expired, which also stops
    // setIsPlaying(true) from restarting playback.
    const store = usePlayerStore.getState();
    store.setSleepTimer(0);

    notificationService.showSleepTimer(0);

    // Clear our own scheduling without calling stopTimer(), which would reset the
    // store's sleepTimer back to null and lose the "expired" state.
    this.timerState = null;
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      logger.error('Error clearing sleep timer state');
    }
  }

  private showNotification(remainingSeconds: number) {
    const minutes = Math.floor(remainingSeconds / 60);

    if (minutes === 5) {
      notificationService.showSleepTimer(5);
    } else if (minutes === 1) {
      notificationService.showSleepTimer(1);
    }
  }

  public startTimer(minutes: number) {
    logger.info(`Starting sleep timer for ${minutes} minutes`);
    this.completed = false;
    this.timerState = {
      startTime: Date.now(),
      duration: minutes * 60 * 1000 // Convert to milliseconds
    };

    this.saveState();
    this.startBackgroundCheck();

    // Update player store
    const store = usePlayerStore.getState();
    store.setSleepTimer(minutes);
  }

  public stopTimer() {
    logger.info('Stopping sleep timer');
    this.completed = false;
    this.timerState = null;
    this.saveState();

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    // Update player store
    const store = usePlayerStore.getState();
    store.setSleepTimer(null);
  }

  /**
   * Seconds left, or null when no timer is running.
   *
   * This used to return `null` once the timer hit zero, which made every expiry
   * check in this file (`remaining !== null && remaining <= 0`) impossible to
   * satisfy — so handleTimerComplete never ran and playback never stopped. It now
   * returns the real value, including zero and negatives, and `null` means only
   * "no timer set".
   */
  public getRemainingTime(): number | null {
    if (!this.timerState) return null;

    const elapsed = Date.now() - this.timerState.startTime;
    return Math.ceil((this.timerState.duration - elapsed) / 1000);
  }

  /** Seconds left clamped at zero, for display. */
  public getDisplayTime(): number | null {
    const remaining = this.getRemainingTime();
    return remaining === null ? null : Math.max(0, remaining);
  }

  public hasExpired(): boolean {
    const remaining = this.getRemainingTime();
    return remaining !== null && remaining <= 0;
  }
}

export const sleepTimerService = new SleepTimerService(); 